import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { PromotionService } from '../../Services/promotion.service';

import Label from '../../components/Label';
import Page from '../../components/Page';
import { useLoadingContext } from '../../context/loadingContext';
import Iconify from '../../components/Iconify';

const ViewPromotion = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [promotion, setPromotion] = useState({});
  const [logs, setLogs] = useState([]);
  const { handleStartLoading, handleStopLoading } = useLoadingContext();

  const switchStatus = useMemo(() => promotion?.status !== 'Inactive', [promotion?.status]);

  useEffect(() => {
    getEventBySlug().finally(handleStopLoading);
  }, []);

  const getEventBySlug = async () => {
    handleStartLoading();
    const { promotion: promotionData, logs } = await PromotionService.GetPromotionBySlug(slug);
    setPromotion(promotionData);
    setLogs(logs);
  };

  const handleDisableClick = () => {
    handleStartLoading();
    PromotionService.DisablePromotion(promotion._id)
      .then(() => {
        setPromotion((prevState) => ({
          ...prevState,
          status: 'Inactive',
        }));
        enqueueSnackbar('Thành công', { variant: 'success' });
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading);
  };

  const handleEnableClick = () => {
    handleStartLoading();
    PromotionService.EnablePromotion(promotion._id)
      .then(() => {
        setPromotion((prevState) => ({
          ...prevState,
          status: 'Active',
        }));
        enqueueSnackbar('Thành công', { variant: 'success' });
      })
      .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
      .finally(handleStopLoading);
  };

  const handleDeleteClick = () => {
    Swal.fire({
      customClass: {
        container: { zIndex: 999999 },
      },
      title: 'Xóa bài viết',
      text: 'Thao tác này không thể hoàn tác',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('sure.yes'),
      cancelButtonText: t('sure.no'),
    }).then((result) => {
      handleStartLoading();
      if (result.isConfirmed) {
        PromotionService.DeletePromotion(promotion._id)
          .then(() => {
            navigate('/promotion');
            enqueueSnackbar('Thành công', { variant: 'success' });
          })
          .catch((err) => enqueueSnackbar(err.response.data, { variant: 'error' }))
          .finally(handleStopLoading);
      }
    });
  };

  const handleCopyClick = (url) => {
    navigator.clipboard.writeText(url).then(() => enqueueSnackbar('Copy thành công', { variant: 'success' }));
  };

  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Page title="page">
            <Card>
              <CardContent>
                <Typography textAlign="center" variant="h4">
                  {promotion && promotion.title}
                </Typography>
                <Typography textAlign="center" mt={2} variant="subtitle1">
                  {promotion && promotion.subTitle}
                </Typography>
                <div className="ck-content" dangerouslySetInnerHTML={{ __html: promotion && promotion.content }} />
              </CardContent>
            </Card>
          </Page>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card>
              <CardHeader title="Chi tiết" />
              <CardContent>
                {promotion?.author?.avatar.match('https://') ? (
                  <Avatar
                    sx={{
                      width: '50px',
                      height: '50px',
                    }}
                    src={promotion?.author?.avatar}
                    alt="photoURL"
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: '50px',
                      height: '50px',
                    }}
                    src={`${process.env.REACT_APP_IMAGE}${promotion?.author?.avatar}`}
                    alt="photoURL"
                  />
                )}

                <Stack direction={'row'}>
                  <Typography fontWeight={700}>
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '400',
                      }}
                    >
                      URL:{' '}
                    </span>
                    {promotion && promotion.slug}
                  </Typography>
                  <IconButton
                    size={'small'}
                    onClick={() => handleCopyClick(`${process.env.REACT_APP_DOMAIN}/khuyen-mai/${promotion.slug}`)}
                  >
                    <Iconify icon="material-symbols:content-copy-outline" />
                  </IconButton>
                </Stack>

                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Username:{' '}
                  </span>
                  {promotion && promotion.author?.username}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Họ và tên:{' '}
                  </span>
                  {promotion && promotion.author?.fullname}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Mã khuyến mãi:{' '}
                  </span>
                  {promotion && promotion.promoCode}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Từ ngày:{' '}
                  </span>
                  {promotion && moment(promotion.fromDateTime).format('DD/MM/YYYY - HH:mm')}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Đến ngày:
                  </span>
                  {promotion && moment(promotion.toDateTime).format('DD/MM/YYYY - HH:mm')}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Trạng thái:{' '}
                  </span>
                  {promotion && (
                    <Label variant="ghost" color={(promotion?.status === 'Inactive' && 'error') || 'success'}>
                      {promotion.status}
                    </Label>
                  )}
                  {promotion && promotion.isHotPromotion && (
                    <Label variant="ghost" color={'success'} sx={{ ml: 1 }}>
                      Khuyễn mại hot
                    </Label>
                  )}
                  {promotion && promotion.showBeforePublic && (
                    <Label variant="ghost" color={'success'} sx={{ ml: 1 }}>
                      Luôn hiển thị
                    </Label>
                  )}
                </Typography>
                <Typography fontWeight={700}>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '400',
                    }}
                  >
                    Ẩn/Hiện:{' '}
                  </span>
                  <Switch
                    checked={switchStatus}
                    onChange={(e) => {
                      if (switchStatus === false) {
                        handleEnableClick();
                      } else {
                        handleDisableClick();
                      }
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </Typography>
                <Stack spacing={1} mt={1}>
                  <Button
                    onClick={() => navigate(`/promotion/edit/${promotion.slug}`)}
                    variant="outlined"
                    fullWidth
                    color="inherit"
                  >
                    Cập nhật
                  </Button>
                  <Button
                    onClick={handleDisableClick}
                    disabled={promotion?.status === 'Inactive'}
                    variant="outlined"
                    fullWidth
                    color="warning"
                  >
                    Ẩn bài viết
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleEnableClick}
                    disabled={promotion?.status === 'Active'}
                  >
                    Hiện bài viết
                  </Button>
                  <Button onClick={handleDeleteClick} fullWidth color="error" variant="outlined">
                    Xóa bài viết
                  </Button>
                </Stack>
              </CardContent>
            </Card>
            <Card>
              <CardHeader title="Logs" />
              <CardContent
                sx={{
                  '& .MuiTimelineItem-missingOppositeContent:before': {
                    display: 'none',
                  },
                }}
              >
                <Timeline>
                  {logs &&
                    logs.map((item, index) => (
                      <OrderItem
                        key={item._id}
                        item={{
                          time: item.time,
                          title: item[lang],
                          type: item.logType,
                          username: item.user.username,
                        }}
                        isLast={index === logs.length - 1}
                      />
                    ))}
                </Timeline>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

// ----------------------------------------------------------------------

OrderItem.propTypes = {
  isLast: PropTypes.bool,
  item: PropTypes.shape({
    time: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    username: PropTypes.string,
  }),
};

function OrderItem({ item, isLast }) {
  const { type, title, time } = item;
  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot
          color={
            (type === 'enable' && 'primary') ||
            (type === 'create' && 'success') ||
            (type === 'reset' && 'info') ||
            (type === 'edit' && 'warning') ||
            (type === 'disable' && 'grey') ||
            'error'
          }
        />
        {isLast ? null : <TimelineConnector />}
      </TimelineSeparator>

      <TimelineContent>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          @{item.username}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {moment(time).format('DD/MM/YYYY-HH:mm')}
        </Typography>
      </TimelineContent>
    </TimelineItem>
  );
}

export default ViewPromotion;
